// 웹 전체 화면(Fullscreen API): 브라우저 주소창·탭을 걷어 게임 화면을 넓힌다(HANDOFF-M1: 태블릿 위 13%를 주소창이 차지).
// 브라우저는 사용자 입력(탭·클릭·키) 안에서만 전체 화면을 허락하므로, 자동 전체 화면도 다음 탭에서 건다.
// 설정 저장: localStorage 'dungeonhero.display' { autoFullscreen }. 기본값은 터치 기기 켬, PC 끔.

const KEY = 'dungeonhero.display';

interface FsDoc { webkitFullscreenElement?: Element | null; webkitFullscreenEnabled?: boolean; webkitExitFullscreen?: () => Promise<void> | void }
interface FsEl { webkitRequestFullscreen?: () => Promise<void> | void }
interface LockableOrientation { lock?: (o: string) => Promise<void> }

/** 이 브라우저에서 문서 전체 화면을 쓸 수 있는가(iPhone Safari는 동영상만 지원해 false) */
export function fullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false;
  const d = document as Document & FsDoc;
  const el = document.documentElement as HTMLElement & FsEl;
  const can = typeof el.requestFullscreen === 'function' || typeof el.webkitRequestFullscreen === 'function';
  const enabled = d.fullscreenEnabled ?? d.webkitFullscreenEnabled ?? true;
  return can && enabled !== false;
}

export function isFullscreen(): boolean {
  if (typeof document === 'undefined') return false;
  const d = document as Document & FsDoc;
  return !!(d.fullscreenElement || d.webkitFullscreenElement);
}

/** 사용자가 직접 끈 뒤에는 이번 실행 동안 자동으로 다시 켜지 않는다 */
let userExited = false;

export async function enterFullscreen(): Promise<boolean> {
  if (!fullscreenSupported() || isFullscreen()) return isFullscreen();
  const el = document.documentElement as HTMLElement & FsEl;
  try {
    if (typeof el.requestFullscreen === 'function') await el.requestFullscreen({ navigationUI: 'hide' });
    else await el.webkitRequestFullscreen?.();
  } catch {
    return false;
  }
  userExited = false;
  // 안드로이드 Chrome은 전체 화면일 때만 가로 고정을 허락한다. 실패해도 게임은 그대로 돈다(세로면 index.html이 화면을 돌린다)
  await lockLandscape();
  return true;
}

/** 지금 가로 고정이 걸려 있다고 아는가(전체 화면을 나가거나 화면이 가려지면 브라우저가 풀므로 그때 지운다) */
let landLocked = false;
/** 연속 실패 횟수: 지원하지 않는 기기에서 탭마다 다시 부르지 않게 3번 실패하면 다음 전체 화면 변화까지 쉰다 */
let lockFails = 0;

/**
 * 가로 고정(던전히어로는 가로 전용, c014). 안드로이드 Chrome은 전체 화면에서만 허락하고, iOS·인앱 웹뷰는 지원하지 않는다.
 * 실패는 조용히 넘긴다: 세로로 든 터치 기기는 index.html이 #app을 90도 돌려 가로로 보여 준다(src/shared/viewport.ts).
 */
export async function lockLandscape(): Promise<boolean> {
  if (landLocked) return true;
  if (lockFails >= 3) return false;
  const so = typeof screen !== 'undefined' ? (screen.orientation as unknown as LockableOrientation | undefined) : undefined;
  if (!so || typeof so.lock !== 'function') return false;
  try {
    await so.lock('landscape');
    landLocked = true;
    lockFails = 0;
  } catch { landLocked = false; lockFails++; }
  return landLocked;
}

/**
 * 터치 기기: 세션 내내 가로를 유지한다. 인트로(index.html)가 첫 탭에 건 전체 화면·가로 고정을 이어받고,
 * 전체 화면에 다시 들어갈 때(설정 버튼·자동 전체 화면·뒤로 가기 뒤 탭)와 앱 전환 뒤 돌아올 때 다시 건다. PC는 아무것도 하지 않는다.
 */
export function installLandscapeKeeper(touch: boolean): void {
  if (!touch || typeof document === 'undefined') return;
  onFullscreenChange((on) => {
    landLocked = false; lockFails = 0;
    if (on) void lockLandscape();
  });
  const back = (): void => {
    if (document.visibilityState === 'hidden') { landLocked = false; lockFails = 0; return; }
    if (isFullscreen()) void lockLandscape();
  };
  document.addEventListener('visibilitychange', back);
  window.addEventListener('pageshow', back);
  // 인트로가 이미 전체 화면·고정을 끝낸 뒤 번들이 늦게 올라온 경우: 고정이 실제로 걸렸는지 알 수 없으니 한 번 더 건다(이미 걸려 있으면 바로 끝난다)
  if (isFullscreen()) void lockLandscape();
}

export async function exitFullscreen(): Promise<void> {
  if (!isFullscreen()) return;
  const d = document as Document & FsDoc;
  userExited = true;
  try {
    if (typeof d.exitFullscreen === 'function') await d.exitFullscreen();
    else await d.webkitExitFullscreen?.();
  } catch { /* 이미 나감 */ }
}

export function toggleFullscreen(): void {
  if (isFullscreen()) void exitFullscreen();
  else void enterFullscreen();
}

export function loadAutoFullscreen(touch: boolean): boolean {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (raw) {
      const j = JSON.parse(raw) as { autoFullscreen?: unknown };
      if (typeof j.autoFullscreen === 'boolean') return j.autoFullscreen;
    }
  } catch { /* 기본값 */ }
  return touch;
}

export function saveAutoFullscreen(on: boolean): void {
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify({ autoFullscreen: on })); } catch { /* 무시 */ }
  if (on) userExited = false;
}

/** 전체 화면 상태가 바뀌면 cb(지금 전체 화면인가) */
export function onFullscreenChange(cb: (on: boolean) => void): void {
  const f = (): void => cb(isFullscreen());
  document.addEventListener('fullscreenchange', f);
  document.addEventListener('webkitfullscreenchange', f);
}

/**
 * 자동 전체 화면: 설정이 켜져 있으면 다음 사용자 탭·클릭에서 전체 화면에 들어간다.
 * 사용자가 전체 화면 버튼·F 키로 직접 나가면 다시 켤 때까지 자동으로 들어가지 않는다(뒤로 가기 제스처로 나간 경우는 다음 탭에서 다시 들어간다).
 */
export function installAutoFullscreen(touch: boolean): void {
  if (!fullscreenSupported()) return;
  const tryEnter = (e: Event): void => {
    // 이미 전체 화면이면 가로 고정만 확인한다(인트로가 들어간 전체 화면에서 고정이 늦게 실패한 경우 등). 걸려 있으면 바로 끝난다
    if (isFullscreen()) { if (touch) void lockLandscape(); return; }
    if (userExited || !loadAutoFullscreen(touch)) return;
    // 전체 화면 버튼·설정의 전체 화면 항목은 스스로 처리한다
    const t = e.target as HTMLElement | null;
    if (t && t.closest && t.closest('[data-fs-control]')) return;
    void enterFullscreen();
  };
  window.addEventListener('pointerup', tryEnter, true);
  window.addEventListener('keydown', (e) => { if (e.code !== 'KeyF' && e.code !== 'Escape') tryEnter(e); }, true);
}
